import { from, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import * as Utils from '@shared/core/utils';
import { Inject, Injectable } from '@angular/core';
import * as Tokens from '@shared/core/tokens';

@Injectable({
    providedIn: 'root',
})
export abstract class BaseGooglePayService {
    protected _instance: Nullable<google.payments.api.PaymentsClient> = null;
    protected _payButton: Nullable<HTMLElement> = null;
    protected _payButtonContainer: Nullable<HTMLElement> = null;
    protected readonly _baseRequest: google.payments.api.baseRequest;
    protected readonly _basePaymentMethod: google.payments.api.IsReadyToPayPaymentMethodSpecification;
    protected readonly _readyToPayRequest: google.payments.api.IsReadyToPayRequest;

    protected constructor(@Inject(Tokens.ENV_TOKEN) protected readonly _env: Environment, @Inject(Tokens.CONFIG_TOKEN) protected readonly _config: OLO.Config) {
        this._baseRequest = this._initBaseRequest();
        this._basePaymentMethod = this._initBasePaymentMethod();
        this._readyToPayRequest = this._initReadyToPayRequest();
    }

    public get isConfigured(): boolean {
        return Utils.VendorPayments.isAppConfiguredForGooglePay(this._config);
    }

    public async setup(
        config: Partial<google.payments.api.PaymentOptions>,
        payButtonContainer: HTMLElement,
        onPayButtonClickCallback: () => void,
    ): Promise<google.payments.api.PaymentsClient | null> {
        this.destroy();

        if (!this.isConfigured) return null;

        this._instance = this._createPaymentsClient(config);

        if ((await this.isReadyToPay().toPromise()) && payButtonContainer) {
            this._payButtonContainer = payButtonContainer;
            this._payButton = this._createPayButton(onPayButtonClickCallback);
            this._payButtonContainer.appendChild(this._payButton);
        }

        return this._instance;
    }

    public abstract pay(totalPrice: string, additionalData?: any): Observable<Nullable<OLO.DTO.GooglePayPaymentData>>;

    public isReadyToPay(): Observable<boolean> {
        if (!this.isConfigured || !this._instance) return from([false]);

        return from(this._instance.isReadyToPay(this._readyToPayRequest)).pipe(
            map((response) => response.result),
            catchError((err) => {
                console.error('Google Pay, isReadyToPay error:', err);

                return from([false]);
            }),
        );
    }

    public destroy(): void {
        this._instance = null;
        if (this._payButton && this._payButtonContainer) {
            this._payButtonContainer.removeChild(this._payButton);
        }
        this._payButton = null;
        this._payButtonContainer = null;
    }

    public async addHtmlElementsToDOM(): Promise<boolean> {
        if (!this.isConfigured) {
            return false;
        }

        return new Promise((resolve) => {
            const scriptElement = document.createElement('script');
            scriptElement.src = '//pay.google.com/gp/p/js/pay.js';

            document.body.appendChild(scriptElement);

            scriptElement.onload = () => resolve(true);
            scriptElement.onerror = () => {
                console.error('Unable to load google-pay script element');
                resolve(false);
            };
        });
    }

    protected _initBaseRequest(): google.payments.api.baseRequest {
        if (!this._config.payments.googlePay) {
            return null;
        }

        return {
            apiVersion: this._config.payments.googlePay.apiVersion,
            apiVersionMinor: this._config.payments.googlePay.apiVersionMinor,
        };
    }

    protected _initBasePaymentMethod(): google.payments.api.IsReadyToPayPaymentMethodSpecification {
        if (!this._config.payments.googlePay) {
            return null;
        }

        return {
            type: 'CARD',
            parameters: {
                allowedAuthMethods: this._config.payments.googlePay.allowedCardAuthMethods,
                allowedCardNetworks: this._config.payments.googlePay.allowedCardNetworks,
            },
        };
    }

    protected _initReadyToPayRequest(): google.payments.api.IsReadyToPayRequest {
        return {
            ...this._baseRequest,
            allowedPaymentMethods: [{ ...this._basePaymentMethod }],
        };
    }

    protected _createPaymentsClient(config: Partial<google.payments.api.PaymentOptions>): google.payments.api.PaymentsClient {
        return new google.payments.api.PaymentsClient({
            environment: this._config.payments.testMode ? 'TEST' : 'PRODUCTION',
            merchantInfo: {
                merchantName: this._config.payments.googlePay.merchantName ?? null,
                merchantId: this._config.payments.googlePay.gatewayMerchantId,
            },
            ...config,
        });
    }

    protected _createPayButton(onPayButtonClickCallback: () => any): HTMLElement {
        return this._instance!.createButton({
            buttonSizeMode: 'fill',
            buttonType: 'plain',
            onClick: onPayButtonClickCallback,
        });
    }

    protected abstract _generatePaymentDataRequestObj(totalPrice: string, additionalData?: any): google.payments.api.PaymentDataRequest;
}
